/**
 * useSynthesisStream — subscribes to /api/synthesis/:id/events and
 * projects SSE events into the same state shape the ChatWindow already
 * renders.
 *
 * Drop-in replacement for the previous useMockSynthesis hook. The
 * surface is almost identical so the consumer component is unchanged.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type LayerKey = 'ingest' | 'recall' | 'research' | 'synthesize' | 'publish';
export type LayerKind = 'pending' | 'active' | 'complete';

export type SimilarQuestion = { id: number; body: string; similarity: number };
export type RelatedInsight = {
  id: number;
  headline: string;
  source?: 'personal' | 'community';
  threadTitle?: string;
};
export type ActivePattern = { label: string; delta: number };
export type Source = { url: string; title: string; domain: string };

export type LayerData = {
  ingest: { theme: string[] };
  recall: {
    similarQuestions: SimilarQuestion[];
    relatedInsights: RelatedInsight[];
    activePatterns: ActivePattern[];
  };
  research: { sources: Source[]; totalPlanned: number };
  synthesize: { streamed: string; confidence: number | null };
  publish: { insightId: number | null; permalink: string | null };
};

export type LayerState<K extends LayerKey> = {
  key: K;
  kind: LayerKind;
  durationMs: number | null;
  data: LayerData[K];
};

export type SynthesisStatus =
  | 'idle'
  | 'connecting'
  | 'running'
  | 'complete'
  | 'canceled'
  | 'error';

export type FinalInsight = {
  id: number;
  number: number;
  headline: string;
  body: string;
  confidence: number;
  tags: string[];
  sourceCount: number;
  mergedCount: number;
  followups: string[];
  sources: Source[];
};

export type SynthesisState = {
  status: SynthesisStatus;
  question: string;
  layers: {
    ingest: LayerState<'ingest'>;
    recall: LayerState<'recall'>;
    research: LayerState<'research'>;
    synthesize: LayerState<'synthesize'>;
    publish: LayerState<'publish'>;
  };
  insight: FinalInsight | null;
  error: string | null;
};

export type UseSynthesisStreamArgs = {
  questionId: number | null;
  /**
   * Fully-qualified SSE path like `/api/threads/12/messages/34/events`.
   * If null, the hook is idle.
   */
  streamPath: string | null;
  question: string;
  run: boolean;
  online?: boolean;
};

const initialLayers = (): SynthesisState['layers'] => ({
  ingest: { key: 'ingest', kind: 'pending', durationMs: null, data: { theme: [] } },
  recall: {
    key: 'recall',
    kind: 'pending',
    durationMs: null,
    data: { similarQuestions: [], relatedInsights: [], activePatterns: [] },
  },
  research: {
    key: 'research',
    kind: 'pending',
    durationMs: null,
    data: { sources: [], totalPlanned: 0 },
  },
  synthesize: {
    key: 'synthesize',
    kind: 'pending',
    durationMs: null,
    data: { streamed: '', confidence: null },
  },
  publish: {
    key: 'publish',
    kind: 'pending',
    durationMs: null,
    data: { insightId: null, permalink: null },
  },
});

const initialState = (question: string): SynthesisState => ({
  status: 'idle',
  question,
  layers: initialLayers(),
  insight: null,
  error: null,
});

export function useSynthesisStream({
  questionId,
  streamPath,
  question,
  run,
}: UseSynthesisStreamArgs): SynthesisState & { cancel: () => void } {
  const [state, setState] = useState<SynthesisState>(() => initialState(question));
  const esRef = useRef<EventSource | null>(null);

  // Single source of truth for "did the server already fire a done event"
  // — guards against EventSource auto-reconnect restarting the stream.
  const doneRef = useRef(false);

  const cancel = useCallback(() => {
    esRef.current?.close();
    esRef.current = null;
    setState((s) => ({ ...s, status: 'canceled' }));
  }, []);

  useEffect(() => {
    if (!run || questionId === null || !streamPath) return;

    doneRef.current = false;
    setState({
      ...initialState(question),
      status: 'connecting',
    });

    const es = new EventSource(streamPath, {
      withCredentials: true,
    });
    esRef.current = es;

    const onOpen = () => {
      setState((s) => (s.status === 'connecting' ? { ...s, status: 'running' } : s));
    };

    const onStageBegin = (e: MessageEvent) => {
      let payload: { stage?: LayerKey };
      try {
        payload = JSON.parse(e.data);
      } catch {
        return;
      }
      const stage = payload.stage;
      if (!stage) return;
      setState((s) => ({
        ...s,
        status: 'running',
        layers: {
          ...s.layers,
          [stage]: { ...s.layers[stage], kind: 'active' as LayerKind },
        },
      }));
    };

    const onStageData = (e: MessageEvent) => {
      let payload: { stage?: LayerKey; data?: Record<string, unknown> };
      try {
        payload = JSON.parse(e.data);
      } catch {
        return;
      }
      const stage = payload.stage;
      const data = payload.data;
      if (!stage || !data) return;
      setState((s) => {
        const existing = s.layers[stage].data as Record<string, unknown>;
        const merged: Record<string, unknown> = { ...existing };

        // For array fields (like research.sources), append instead of
        // replacing. This lets the server stream sources one-by-one.
        for (const [key, value] of Object.entries(data)) {
          if (Array.isArray(value) && Array.isArray(existing[key])) {
            merged[key] = [...(existing[key] as unknown[]), ...value];
          } else {
            merged[key] = value;
          }
        }

        return {
          ...s,
          layers: {
            ...s.layers,
            [stage]: {
              ...s.layers[stage],
              data: merged as typeof s.layers[typeof stage]['data'],
            },
          },
        };
      });
    };

    const onStageEnd = (e: MessageEvent) => {
      let payload: { stage?: LayerKey; durationMs?: number };
      try {
        payload = JSON.parse(e.data);
      } catch {
        return;
      }
      const stage = payload.stage;
      if (!stage) return;
      setState((s) => ({
        ...s,
        layers: {
          ...s.layers,
          [stage]: {
            ...s.layers[stage],
            kind: 'complete' as LayerKind,
            durationMs: payload.durationMs ?? null,
          },
        },
      }));
    };

    const onToken = (e: MessageEvent) => {
      let payload: { text?: string };
      try {
        payload = JSON.parse(e.data);
      } catch {
        return;
      }
      const text = payload.text;
      if (typeof text !== 'string') return;
      setState((s) => ({
        ...s,
        layers: {
          ...s.layers,
          synthesize: {
            ...s.layers.synthesize,
            data: {
              ...s.layers.synthesize.data,
              streamed: s.layers.synthesize.data.streamed + text,
            },
          },
        },
      }));
    };

    const onDone = (e: MessageEvent) => {
      doneRef.current = true;
      let payload: {
        insight?: {
          id: number;
          headline: string;
          body: string;
          followups?: string[];
          sources?: Source[];
        };
      };
      try {
        payload = JSON.parse(e.data);
      } catch {
        return;
      }
      if (!payload.insight) return;
      const sources = payload.insight.sources ?? [];
      setState((s) => ({
        ...s,
        status: 'complete',
        insight: {
          id: payload.insight!.id,
          number: payload.insight!.id,
          headline: payload.insight!.headline,
          body: payload.insight!.body,
          confidence: 0.8,
          tags: [],
          sourceCount: sources.length,
          mergedCount: 0,
          followups: payload.insight!.followups ?? [],
          sources,
        },
      }));
      es.close();
      esRef.current = null;
    };

    const onServerError = (e: MessageEvent) => {
      // Custom 'error' event with payload (name collision with built-in
      // onerror — we listen to both).
      let message = 'Synthesis failed';
      try {
        const payload = JSON.parse(e.data) as { message?: string };
        if (payload.message) message = payload.message;
      } catch {
        /* ignore */
      }
      setState((s) => ({ ...s, status: 'error', error: message }));
      es.close();
      esRef.current = null;
    };

    const onTransportError = () => {
      // Built-in error event (connection/parse error). Only surface if
      // the stream hasn't already completed — EventSource can fire this
      // when it reconnects or after a clean close.
      if (doneRef.current) return;
      if (es.readyState === EventSource.CLOSED) {
        setState((s) => ({
          ...s,
          status: s.status === 'complete' ? s.status : 'error',
          error: s.error ?? 'Lost connection to the synthesizer.',
        }));
      }
    };

    es.addEventListener('open', onOpen);
    es.addEventListener('stage-begin', onStageBegin as EventListener);
    es.addEventListener('stage-data', onStageData as EventListener);
    es.addEventListener('stage-end', onStageEnd as EventListener);
    es.addEventListener('token', onToken as EventListener);
    es.addEventListener('done', onDone as EventListener);
    es.addEventListener('error', onServerError as EventListener);
    es.onerror = onTransportError;

    return () => {
      es.removeEventListener('open', onOpen);
      es.removeEventListener('stage-begin', onStageBegin as EventListener);
      es.removeEventListener('stage-data', onStageData as EventListener);
      es.removeEventListener('stage-end', onStageEnd as EventListener);
      es.removeEventListener('token', onToken as EventListener);
      es.removeEventListener('done', onDone as EventListener);
      es.removeEventListener('error', onServerError as EventListener);
      es.close();
      esRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId, streamPath, run]);

  return useMemo(() => ({ ...state, cancel }), [state, cancel]);
}
